import { RichText } from '@graphcms/rich-text-react-renderer'
import Image from 'next/image'
import React, { useMemo } from 'react'
import { IRichText } from 'src/types/hygraph'

import { RichNode } from './toc'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isEmptyP = (children: any) => {
  const content = children?.props?.content ?? []
  const empty = content?.length === 1 && content[0]?.text === ''
  if (empty) return true
  return false
}

const idOfHeading = (map: WeakMap<object, string>, children: unknown) => {
  const nodes = React.isValidElement<{ content?: unknown }>(children)
    ? children.props.content
    : undefined
  return nodes && typeof nodes === 'object' ? map.get(nodes) : undefined
}

type IProps = {
  content: IRichText
  /** Anchor ids for each `heading-two` in document order (see Help/toc.ts). */
  headingIds?: string[]
}

/**
 * RichText renderer for blog + help articles.
 *
 * MUST render on the server: the article body is the page's indexable content.
 * A client-only gate (2026-03 → 2026-09-30) shipped every article to crawlers
 * with ~60 words instead of the full body — non-JS crawlers (GPTBot,
 * PerplexityBot, ClaudeBot) saw nothing. Never reintroduce a mount gate here;
 * fix any hydration mismatch at the renderer that causes it.
 */
const RichTextContainer = ({ content, headingIds }: IProps) => {
  // Map each `heading-two` node to its precomputed anchor id, keyed on the
  // node's `children` array — the renderer hands that same array to the `h2`
  // renderer (as `children.props.content`). A render-time counter drifted under
  // StrictMode's double render and caused a server/client id mismatch.
  const idByHeading = useMemo(() => {
    const map = new WeakMap<object, string>()
    const nodes = (
      (content?.raw as { children?: RichNode[] })?.children ?? []
    ).filter((n) => n.type === 'heading-two')
    nodes.forEach((n, i) => {
      const id = headingIds?.[i]
      if (id && n.children) map.set(n.children, id)
    })
    return map
  }, [content, headingIds])

  return (
    <div className="max-w-none">
      {content?.raw ? (
        <RichText
          content={content?.raw}
          renderers={{
            a: ({ children, href, openInNewTab, rel, ...rest }) => (
              <a
                href={href}
                target={openInNewTab ? '_blank' : undefined}
                rel={openInNewTab ? 'noopener noreferrer' : rel}
                className="border-b border-blue-500/40 text-blue-500 transition-colors hover:border-blue-500 hover:text-blue-600"
                {...rest}
              >
                {children}
              </a>
            ),
            h1: ({ children }) => (
              <h2 className="mt-12 mb-4 font-display text-[26px] leading-[1.15] font-medium tracking-[-0.01em] text-ink-700 md:text-[34px]">
                {children}
              </h2>
            ),
            h2: ({ children }) => (
              <h2
                id={idOfHeading(idByHeading, children)}
                className="mt-10 mb-4 scroll-mt-[calc(var(--nav-h)+24px)] font-display text-[24px] leading-[1.15] font-medium tracking-[-0.01em] text-ink-700 md:text-[30px]"
              >
                {children}
              </h2>
            ),
            h3: ({ children }) => (
              <h3 className="mt-8 mb-3 font-display text-[20px] leading-[1.2] font-medium text-ink-700 md:text-[24px]">
                {children}
              </h3>
            ),
            h4: ({ children }) => (
              <h4 className="mt-6 mb-3 font-display text-[18px] leading-[1.25] font-medium text-ink-700 md:text-[20px]">
                {children}
              </h4>
            ),
            h5: ({ children }) => (
              <h5 className="mt-5 mb-3 font-display text-[16px] font-medium text-ink-700 md:text-[18px]">
                {children}
              </h5>
            ),
            h6: ({ children }) => (
              <h6 className="mt-5 mb-3 font-display text-[14px] font-medium tracking-[0.04em] text-ink-700 uppercase md:text-[15px]">
                {children}
              </h6>
            ),
            p: ({ children }) =>
              isEmptyP(children) ? (
                <br />
              ) : (
                <p className="mb-5 text-[15px] leading-[1.75] text-ink-200 md:text-[17px]">
                  {children}
                </p>
              ),
            ul: ({ children }) => (
              <ul className="my-6 list-disc space-y-2 pl-6 text-ink-200 marker:text-ink-200/60">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="my-6 list-decimal space-y-2 pl-6 text-ink-200 marker:font-display marker:font-medium marker:text-blue-500 marker:italic">
                {children}
              </ol>
            ),
            li: ({ children }) => (
              <li className="text-[15px] leading-[1.7] md:text-[17px]">
                {children}
              </li>
            ),
            blockquote: ({ children }) => (
              <blockquote className="my-8 border-l-2 border-blue-500 pl-6">
                <div className="font-display text-[20px] leading-[1.4] font-medium text-ink-700 italic md:text-[24px]">
                  {children}
                </div>
              </blockquote>
            ),
            img: ({ src, altText }) => (
              <figure className="my-8">
                <div className="relative aspect-video w-full overflow-hidden border border-ink-100/15 bg-cream-300">
                  <Image
                    src={src ?? '/images/empty.jpeg'}
                    alt={altText ?? ''}
                    fill
                    sizes="(max-width: 768px) 100vw, 800px"
                    quality={75}
                    className="object-cover"
                    loading="lazy"
                  />
                </div>
                {altText && (
                  <figcaption className="mt-3 font-display text-[12px] tracking-[0.04em] text-ink-200 md:text-[13px]">
                    {altText}
                  </figcaption>
                )}
              </figure>
            ),
            table_head: ({ children }) => (
              <thead className="border-b border-ink-100/15 bg-cream-300">
                {children}
              </thead>
            ),
            table_header_cell: ({ children }) => (
              <th className="px-4 py-3 text-left font-display text-[13px] font-medium tracking-[0.04em] text-ink-700 uppercase">
                {children}
              </th>
            ),
            table_cell: ({ children }) => (
              <td className="px-4 py-3 text-[14px] text-ink-200 md:text-[15px]">
                {children}
              </td>
            ),
            table_body: ({ children }) => (
              <tbody className="divide-y divide-ink-100/15 bg-white-500">
                {children}
              </tbody>
            ),
            table_row: ({ children }) => (
              <tr className="transition-colors hover:bg-cream-300/40">
                {children}
              </tr>
            ),
            table: ({ children }) => (
              <div className="my-8 overflow-x-auto border border-ink-100/15">
                <table className="min-w-full border-collapse">{children}</table>
              </div>
            ),
            code_block: ({ children }) => (
              <pre className="font-mono my-6 overflow-x-auto border border-ink-100/15 bg-cream-300 p-5 text-[13px] leading-relaxed text-ink-700 md:text-[14px]">
                <code>{children}</code>
              </pre>
            ),
            code: ({ children }) => (
              <code className="font-mono border border-ink-100/15 bg-cream-300 px-1.5 py-0.5 text-[13px] text-ink-700">
                {children}
              </code>
            ),
            bold: ({ children }) => (
              <span className="font-medium text-ink-700">{children}</span>
            ),
            italic: ({ children }) => <em className="italic">{children}</em>,
            underline: ({ children }) => (
              <u className="underline">{children}</u>
            ),
            Asset: {
              image: ({ url, altText }) => (
                <figure className="my-8">
                  <div className="relative aspect-video w-full overflow-hidden border border-ink-100/15 bg-cream-300">
                    <Image
                      src={url ?? '/images/empty.jpeg'}
                      alt={altText ?? ''}
                      fill
                      sizes="(max-width: 768px) 100vw, 800px"
                      quality={75}
                      className="object-cover"
                      loading="lazy"
                    />
                  </div>
                  {altText && (
                    <figcaption className="mt-3 font-display text-[12px] tracking-[0.04em] text-ink-200 md:text-[13px]">
                      {altText}
                    </figcaption>
                  )}
                </figure>
              ),
              video: ({ url, title }) => (
                <div className="my-8 aspect-video w-full overflow-hidden border border-ink-100/15">
                  <video
                    src={url}
                    title={title}
                    controls
                    className="size-full"
                  />
                </div>
              )
            }
          }}
        />
      ) : null}
    </div>
  )
}

export default React.memo(RichTextContainer)
